<template>
  <div class="org-stripe">
    <!-- Header: what these keys are and which Stripe mode they point at -->
    <div class="org-stripe__head">
      <div class="org-stripe__head-text">
        <h4 class="mb-25">
          <i class="fab fa-cc-stripe mr-50"/>{{ msg('Stripe credentials') }}
        </h4>
        <p class="text-muted mb-0">
          {{ msg('Organization-wide Stripe keys used wherever a building has none of its own') }}
        </p>
      </div>
      <div class="org-stripe__mode" :class="`org-stripe__mode--${mode}`">
        <i :class="mode === 'live' ? 'fas fa-circle' : 'far fa-circle'" class="mr-50"/>
        {{ mode === 'live' ? msg('Live mode') : mode === 'test' ? msg('Test mode') : msg('Not configured') }}
      </div>
    </div>

    <!-- Live keys move real money — say so before the fields, not after -->
    <b-alert v-if="mode === 'live'" show variant="warning" class="org-stripe__alert">
      <i class="fas fa-exclamation-triangle mr-50"/>
      {{ msg('These are live keys. Charges made with them are real.') }}
    </b-alert>

    <b-row>
      <b-col
          v-for="field in fields"
          :key="field.key"
          cols="12"
          lg="4"
      >
        <b-form-group :label="msg(field.label)" :label-for="`org-${field.key}`">
          <b-input-group>
            <b-form-input
                :id="`org-${field.key}`"
                v-model="org[field.key]"
                :type="field.secret && !revealed[field.key] ? 'password' : 'text'"
                :readonly="isLocked(field.key)"
                :state="stateOf(field)"
                :placeholder="field.placeholder"
                autocomplete="off"
                spellcheck="false"
            />
            <b-input-group-append v-if="field.secret">
              <b-button
                  variant="outline-secondary"
                  :title="revealed[field.key] ? msg('Hide') : msg('Show')"
                  @click="toggleReveal(field.key)"
              >
                <feather-icon :icon="revealed[field.key] ? 'EyeOffIcon' : 'EyeIcon'"/>
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <!-- one line under each field: the prefix warning wins over the lock note -->
          <small v-if="stateOf(field) === false" class="text-danger">
            {{ msg('Key should start with') }} <code>{{ field.prefix }}</code>
          </small>
          <small v-else-if="isLocked(field.key)" class="text-muted">
            <i class="fas fa-lock mr-25"/>{{ msg('Token is set — locked for safety') }}
          </small>
          <small v-else class="text-muted">{{ msg(field.hint) }}</small>
        </b-form-group>
      </b-col>
    </b-row>

    <div class="d-flex align-items-center justify-content-between flex-wrap mt-1">
      <small class="text-muted">
        <i class="fas fa-shield-alt mr-25"/>
        {{ msg('The secret key never leaves the server session and is not sent to guests') }}
      </small>
      <b-button variant="primary" :disabled="hasInvalidKey" @click="$emit('save')">
        {{ msg('save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'orgStripe',
  props: {
    value: Object,
    /**
     * Admins may rotate a key in place. For everyone else a populated field
     * locks, matching buildingManagement — an accidental keystroke should not
     * be able to silently break a working payment route.
     */
    canRotate: { type: Boolean, default: false }
  },
  data() {
    return {
      revealed: { stripeApiKey: false, stripeRestrictedApiKey: false },
      fields: [
        {
          key: 'stripeApiKey',
          label: 'Stripe Token',
          hint: 'Server-side secret key',
          prefix: 'sk_',
          placeholder: 'sk_live_…',
          secret: true
        },
        {
          key: 'stripePublicApiKey',
          label: 'Stripe Public Token',
          hint: 'Publishable key used in the browser',
          prefix: 'pk_',
          placeholder: 'pk_live_…',
          secret: false
        },
        {
          key: 'stripeRestrictedApiKey',
          label: 'Stripe Restricted Token',
          hint: 'Narrow-scope key for integrations',
          prefix: 'rk_',
          placeholder: 'rk_live_…',
          secret: true
        }
      ]
    }
  },
  computed: {
    org: {
      get() {
        if (this.value == null) {
          console.error('orgStripe: value (v-model) is null!')
          return {}
        }
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    /**
     * Derived from the keys themselves rather than stored — Stripe encodes the
     * mode in the prefix, so there is nothing to keep in sync. The secret key
     * decides; the publishable key is the fallback when only it is filled.
     */
    mode() {
      const probe = this.trimmed('stripeApiKey') || this.trimmed('stripePublicApiKey')
      if (probe === '') return 'none'
      if (probe.indexOf('_live_') >= 0) return 'live'
      if (probe.indexOf('_test_') >= 0) return 'test'
      return 'none'
    },
    hasInvalidKey() {
      return this.fields.some(f => this.stateOf(f) === false)
    }
  },
  methods: {
    trimmed(key) {
      const v = this.org[key]
      return v == null ? '' : String(v).trim()
    },
    isLocked(key) {
      if (this.canRotate) return false
      return this.trimmed(key) !== ''
    },
    toggleReveal(key) {
      this.$set(this.revealed, key, !this.revealed[key])
    },
    /** null while empty — an untouched field should look neutral, not wrong. */
    stateOf(field) {
      const v = this.trimmed(field.key)
      if (v === '') return null
      return v.indexOf(field.prefix) === 0 ? null : false
    }
  }
}
</script>

<style lang="scss" scoped>
.org-stripe {
  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 1rem;
  }

  &__head-text p {
    max-width: 60ch;
  }

  &__mode {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 999px;
    font-size: .82rem;
    font-weight: 600;
    white-space: nowrap;
    background: rgba(115, 103, 240, .12);
    color: #7367f0;

    &--live {
      background: rgba(40, 199, 111, .12);
      color: #28c76f;
    }

    &--test {
      background: rgba(255, 159, 67, .12);
      color: #ff9f43;
    }

    &--none {
      background: rgba(130, 134, 139, .12);
      color: #82868b;
    }
  }

  &__alert {
    padding: .6rem .9rem;
    font-size: .9rem;
  }

  code {
    font-size: .85em;
  }
}
</style>
